import { GetStaticProps } from 'next';
import URL from '@/common/app/utils/urlsUtils';
import {
    VisitorsGuide,
    VisitorsGuideTypes,
} from '@/screens/DestinationsPage/ui/VisitorsGuide/VisitorsGuide';
import { fetchBlogsByCategory } from '@/entities/Blogs/service';
import { getFAQ } from '@/shared/FAQ/service/ApiFaq';
import { getVisitorsGuide, fetchDestinations } from '@/common/service/api/Destination/Destination';
import { getCategoriesFeatured } from '@/shared/TopAttractions/service/ApiTopAttractions';
import { fetchAppCategories } from '@/common/service/api/Categories/Categories';
import { ICategory } from '@/common/service/api/Categories/Categories.domain';
import { getRedirectsPath } from '@/common/service/api/Redirects/getRedirectsPath';
import { createDestinationMeta } from '@/entities/Attractions/service/Creators';
import { fetchLandmarksByCityId } from '@/screens/LandmarkMainPage/service/fetchLandmarksByCityId';
import { getRecommends } from '@/shared/FeaturedActivities/service/ApiFeaturedActivities';
import { getPlaceholders } from '@/common/service/api/Activity/Activity';

type paramsProps = {
    state: string;
    destination: string;
};

export async function getStaticPaths() {
    const destinations = await fetchDestinations();
    const paths = destinations.map(({ state, slug }) => ({
        params: {
            state: state.code.toLowerCase(),
            destination: slug.toLowerCase(),
        },
    }));
    return {
        paths,
        fallback: 'blocking',
    };
}

export const getStaticProps: GetStaticProps<VisitorsGuideTypes, paramsProps> = async ({
    params,
}) => {
    try {
        if (!params) {
            throw new Error(`PAGE DESTINATION SLUG: BAD PARAMS: ${params}`);
        }

        const destinations = await fetchDestinations();
        const slug = params.destination.toLowerCase();
        const state = params.state.toUpperCase();

        const destination = destinations.find(
            (destination) =>
                destination.slug.toLowerCase() === slug && destination.state.code === state
        );
        const currentPath = `/destination/${params.state}/${params.destination}/`;

        if (!destination) {
            const redirectPath = await getRedirectsPath(currentPath);

            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: redirectPath.toLowerCase(),
                    permanent: true,
                },
            };
        }

        const rightPath =
            `/destination/${destination.state.code}/${destination.slug}/`.toLowerCase();

        if (currentPath !== rightPath) {
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: rightPath,
                    permanent: true,
                },
            };
        }

        destination.url = rightPath;

        const faqSlug = `destination_${destination.state.code}_${destination.slug}`;

        const categoriesAsObject = destination.categories?.split(',').map((item) => ({
            id: item.split(':')[0],
            countActivities: item.split(':')[1] || null,
        }));

        const [pageDestination, blogs, landmarks, faq, featuredCategories, recommends] =
            await Promise.all([
                getVisitorsGuide(destination.id),
                fetchBlogsByCategory({ category_id: destination.id, limit: 6 }),
                fetchLandmarksByCityId(destination.id),
                getFAQ(faqSlug),
                getCategoriesFeatured(destination.id),
                getRecommends({
                    destination_id: destination.id,
                }),
            ]);

        if (featuredCategories.length) {
            featuredCategories.forEach((category) => {
                category.url = rightPath + 'things-to-do/' + category.slug + '/';
            });
        }

        const categoriesList = await fetchAppCategories();

        const categoryArr: ICategory[] = [];

        categoriesList?.forEach((cat) => {
            categoriesAsObject.forEach((catObj) => {
                if (cat.id === catObj.id) {
                    categoryArr.push(cat);
                }
            });
        });

        const images = [
            `${URL.image}/destination/${pageDestination.id}/${pageDestination.slug}.jpg`,
            `${URL.image}/destination/${pageDestination.id}/${pageDestination.slug}.webp`,
        ];

        if (!pageDestination || !pageDestination.meta) {
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: `/404/`,
                    permanent: true,
                },
            };
        }

        // Update destination page meta to new template
        pageDestination.meta = createDestinationMeta(pageDestination);

        const breadcrumbs = [
            {
                url: '/destination/',
                title: 'Destination',
            },
            {
                url: `/destination/${pageDestination.state.code.toLowerCase()}/`,
                title: pageDestination.state.name,
            },
            {
                url: rightPath,
                title: pageDestination.name,
                unlink: true,
            },
        ];

        const placeholders = await getPlaceholders(
            recommends.map(({ id }) => id),
            'activity'
        );

        recommends.forEach((i) => {
            i.placeholder = placeholders[i.id] || '';
        });

        return {
            revalidate: Number(process.env.CACHE_ENV),
            props: {
                destination: destination,
                pageDestination,
                featuredCategories: featuredCategories,
                categories: categoryArr,
                blogs,
                landmarks,
                faq,
                images,
                currentDestination: destination,
                breadcrumbs,
                recommendedTrips: recommends,
                recommendedIds: recommends.map(({id}) => id)
            },
        };
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export default VisitorsGuide;
